// ═══════════════════════════════════════════════════════════════════════════
// PanneauRegles.jsx — Règles & Feature Flags — PesacoreIH
// ═══════════════════════════════════════════════════════════════════════════

import {
    AlertTriangle, CheckCircle, Euro, Flag,
    Layers, Plus, RefreshCw, RotateCcw,
    Search, Shield, ToggleLeft, ToggleRight,
    Trash2, X, Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';

// ─── Config catégories ────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all',          label: 'Toutes',        icon: Layers  },
  { id: 'feature-flag', label: 'Feature Flags', icon: Flag    },
  { id: 'quota',        label: 'Quotas',        icon: Zap     },
  { id: 'pricing',      label: 'Tarifs',        icon: Euro    },
  { id: 'waf',          label: 'WAF',           icon: Shield  },
  { id: 'business',     label: 'Métier',        icon: CheckCircle },
];

const PLANS = ['freemium','academia','pro','entreprise'];

// ─── Composants UI ────────────────────────────────────────────────────────

function Badge({ children, color = 'zinc' }) {
  const map = {
    zinc:    'bg-zinc-800 text-zinc-400 border-zinc-700',
    emerald: 'bg-emerald-950/40 text-emerald-300 border-emerald-800/40',
    amber:   'bg-amber-950/40 text-amber-300 border-amber-800/40',
    rose:    'bg-rose-950/40 text-rose-300 border-rose-800/40',
    sky:     'bg-sky-950/40 text-sky-300 border-sky-800/40',
    orange:  'bg-orange-950/40 text-orange-300 border-orange-800/40',
    purple:  'bg-purple-950/40 text-purple-300 border-purple-800/40',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-mono ${map[color]}`}>{children}</span>;
}

function Feedback({ msg, type, onClear }) {
  if (!msg) return null;
  const s = { success:'bg-emerald-950/30 border-emerald-800/30 text-emerald-300', error:'bg-red-950/30 border-red-800/30 text-red-300', info:'bg-sky-950/30 border-sky-800/30 text-sky-300' };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded border text-xs ${s[type]||s.info}`}>
      <span>{msg}</span>
      {onClear && <button onClick={onClear}><X size={12}/></button>}
    </div>
  );
}

// ─── Hook données ─────────────────────────────────────────────────────────

function useRulesData() {
  const [rules,   setRules]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetchAll = async (category = '') => {
    setLoading(true);
    try {
      const r = await api.get('/superadmin/rules' + (category && category !== 'all' ? `?category=${category}` : ''));
      setRules(r.data.rules || []);
      setSummary(r.data.summary || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRule = async (category, key, update) => {
    const r = await api.put(`/superadmin/rules/${category}/${key}`, update);
    // Mise à jour locale optimiste
    setRules(prev => prev.map(rule =>
      rule.category === category && rule.key === key ? { ...rule, ...update } : rule
    ));
    return r.data;
  };

  const createRule = async (data) => {
    const r = await api.post('/superadmin/rules', data);
    await fetchAll();
    return r.data;
  };

  const deleteRule = async (category, key) => {
    const r = await api.delete(`/superadmin/rules/${category}/${key}`);
    setRules(prev => prev.filter(rule => !(rule.category === category && rule.key === key)));
    return r.data;
  };

  const reloadCache = async () => {
    const r = await api.post('/superadmin/rules/reload');
    await fetchAll();
    return r.data;
  };

  const blockIp = async (ip, reason) => {
    const r = await api.post('/superadmin/rules/waf/block-ip', { ip, reason });
    await fetchAll('waf');
    return r.data;
  };

  const unblockIp = async (ip) => {
    const r = await api.delete(`/superadmin/rules/waf/block-ip/${encodeURIComponent(ip)}`);
    await fetchAll('waf');
    return r.data;
  };

  useEffect(() => { fetchAll(); }, []);

  return { rules, summary, loading, error, fetchAll, updateRule, createRule, deleteRule, reloadCache, blockIp, unblockIp };
}

// ─── Ligne règle éditable ─────────────────────────────────────────────────

function RuleRow({ rule, onUpdate, onDelete }) {
  const [editing,  setEditing]  = useState(false);
  const [valRaw,   setValRaw]   = useState('');
  const [saving,   setSaving]   = useState(false);
  const [feedback, setFeedback] = useState(null);

  const isDefault = !rule.createdBy || rule.createdBy === 'system';
  const isFlag    = rule.category === 'feature-flag';
  const isArray   = Array.isArray(rule.value);
  const isObject  = typeof rule.value === 'object' && !isArray && rule.value !== null;

  const displayValue = () => {
    if (isArray)  return `[${rule.value.length} éléments]`;
    if (isObject) return JSON.stringify(rule.value);
    if (typeof rule.value === 'boolean') return rule.value ? 'true' : 'false';
    return String(rule.value);
  };

  const parseValue = (raw) => {
    if (raw === 'true')  return true;
    if (raw === 'false') return false;
    if (raw === 'null')  return null;
    const num = Number(raw);
    if (!isNaN(num) && raw.trim() !== '') return num;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(rule.category, rule.key, { value: parseValue(valRaw) });
      setFeedback({ type: 'success', msg: 'Sauvegardé' });
      setEditing(false);
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  const handleToggle = async () => {
    try {
      await onUpdate(rule.category, rule.key, {
        ...(isFlag ? { value: !rule.value } : { active: !rule.active })
      });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  return (
    <div className={`border-b border-zinc-800/30 last:border-0 transition-all ${!rule.active ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800/10 group">

        {/* Toggle actif */}
        <button onClick={handleToggle} className="shrink-0">
          {(isFlag ? rule.value : rule.active)
            ? <ToggleRight size={18} className="text-emerald-500" />
            : <ToggleLeft  size={18} className="text-zinc-600"    />
          }
        </button>

        {/* Key */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-zinc-300">{rule.key}</span>
            {rule.targets?.plans?.length > 0 && rule.targets.plans.map(p => (
              <Badge key={p} color="sky">{p}</Badge>
            ))}
            {rule.targets?.roles?.length > 0 && rule.targets.roles.map(r => (
              <Badge key={r} color="purple">{r}</Badge>
            ))}
          </div>
          {rule.label && <p className="text-[10px] text-zinc-600 mt-0.5">{rule.label}</p>}
        </div>

        {/* Valeur */}
        <div className="shrink-0">
          {editing ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={valRaw}
                onChange={e => setValRaw(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
                className="w-36 bg-black border border-orange-700/50 rounded px-2 py-1 text-xs font-mono text-zinc-200 outline-none"
              />
              <button onClick={handleSave} disabled={saving}
                className="px-2 py-1 bg-orange-600/30 border border-orange-500/40 rounded text-orange-200 text-[10px] disabled:opacity-40">
                {saving ? '...' : 'OK'}
              </button>
              <button onClick={() => setEditing(false)}
                className="p-1 text-zinc-600 hover:text-zinc-300">
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setValRaw(isArray || isObject ? JSON.stringify(rule.value) : String(rule.value)); setEditing(true); }}
              className="px-3 py-1 bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700/40 rounded text-xs font-mono text-zinc-300 max-w-40 truncate"
              title={String(rule.value)}
            >
              {displayValue()}
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 hidden group-hover:flex items-center gap-1">
          {!isDefault && (
            <button onClick={() => onDelete(rule.category, rule.key)}
              className="p-1 text-zinc-700 hover:text-red-400">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div className="px-4 pb-2">
          <Feedback {...feedback} onClear={() => setFeedback(null)} />
        </div>
      )}
    </div>
  );
}

// ─── Section WAF spéciale ─────────────────────────────────────────────────

function SectionWAF({ rules, onUpdate, onBlockIp, onUnblockIp }) {
  const [newIp,    setNewIp]    = useState('');
  const [reason,   setReason]   = useState('');
  const [feedback, setFeedback] = useState(null);

  const blockedEntry = rules.find(r => r.key === 'blocked-ips');
  const blockedIps   = Array.isArray(blockedEntry?.value) ? blockedEntry.value : [];
  const otherRules   = rules.filter(r => r.key !== 'blocked-ips');

  const handleBlock = async () => {
    if (!newIp.trim()) return;
    try {
      await onBlockIp(newIp.trim(), reason);
      setNewIp('');
      setReason('');
      setFeedback({ type: 'success', msg: `IP ${newIp} bloquée` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.error || err.message });
    }
    setTimeout(() => setFeedback(null), 3000);
  };

  return (
    <div className="space-y-4">
      {/* Règles WAF standard */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
        {otherRules.map(rule => (
          <RuleRow key={`${rule.category}:${rule.key}`} rule={rule} onUpdate={onUpdate} onDelete={() => {}} />
        ))}
      </div>

      {/* Gestion IPs bloquées */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-2">
          <Shield size={11} />
          IPs bloquées ({blockedIps.length})
        </div>

        {feedback && <div className="px-4 pt-2"><Feedback {...feedback} onClear={() => setFeedback(null)} /></div>}

        {/* Ajouter une IP */}
        <div className="px-4 py-3 flex gap-2 border-b border-zinc-800/30">
          <input value={newIp} onChange={e => setNewIp(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleBlock()}
            placeholder="IP à bloquer (ex: 1.2.3.4)"
            className="flex-1 bg-black border border-zinc-700 rounded px-3 py-1.5 text-xs font-mono text-zinc-200 focus:border-red-600/50 outline-none" />
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Raison (optionnel)"
            className="w-40 bg-black border border-zinc-700 rounded px-3 py-1.5 text-xs text-zinc-400 focus:border-zinc-600 outline-none" />
          <button onClick={handleBlock} disabled={!newIp.trim()}
            className="px-3 py-1.5 bg-red-700/30 border border-red-600/30 rounded text-red-300 text-xs disabled:opacity-30">
            Bloquer
          </button>
        </div>

        {/* Liste IPs */}
        <div className="divide-y divide-zinc-800/20 max-h-48 overflow-y-auto">
          {blockedIps.length === 0 ? (
            <p className="px-4 py-3 text-zinc-700 text-xs">Aucune IP bloquée</p>
          ) : (
            blockedIps.map(ip => (
              <div key={ip} className="flex items-center justify-between px-4 py-2">
                <span className="text-xs font-mono text-zinc-300">{ip}</span>
                <button onClick={() => onUnblockIp(ip)}
                  className="flex items-center gap-1 text-[10px] text-zinc-600 hover:text-emerald-400">
                  <RotateCcw size={11} />
                  Débloquer
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Formulaire nouvelle règle ────────────────────────────────────────────

function FormNouvelleRegle({ onSubmit, onClose }) {
  const [form, setForm] = useState({
    category: 'feature-flag', key: '', value: 'false',
    label: '', description: '', plans: [], roles: [],
  });
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const togglePlan = (p) => set('plans', form.plans.includes(p) ? form.plans.filter(x => x !== p) : [...form.plans, p]);

  const parseValue = (raw) => {
    if (raw === 'true')  return true;
    if (raw === 'false') return false;
    const num = Number(raw);
    if (!isNaN(num) && raw.trim() !== '') return num;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const handleSubmit = async () => {
    setErr(null);
    if (!form.key.trim())      return setErr('Key obligatoire');
    if (!form.category.trim()) return setErr('Catégorie obligatoire');
    try {
      await onSubmit({
        category: form.category,
        key:      form.key.trim().toLowerCase().replace(/\s+/g, '-'),
        value:    parseValue(form.value),
        label:    form.label,
        description: form.description,
        targets:  { plans: form.plans, roles: form.roles },
      });
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-orange-800/30 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">Nouvelle règle</h4>
        <button onClick={onClose}><X size={14} className="text-zinc-500 hover:text-white" /></button>
      </div>
      {err && <div className="px-3 py-2 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs">{err}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Catégorie</label>
          <select value={form.category} onChange={e => set('category', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 outline-none focus:border-orange-600/50">
            {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Key</label>
          <input value={form.key} onChange={e => set('key', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 outline-none focus:border-orange-600/50"
            placeholder="ex: nouvelle-feature" />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Valeur</label>
          <input value={form.value} onChange={e => set('value', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 outline-none focus:border-orange-600/50"
            placeholder="true / false / 42 / &quot;texte&quot; / []" />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Label</label>
          <input value={form.label} onChange={e => set('label', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 outline-none focus:border-orange-600/50"
            placeholder="Nom lisible" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Description</label>
          <input value={form.description} onChange={e => set('description', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 outline-none focus:border-orange-600/50" />
        </div>
        {form.category === 'feature-flag' && (
          <div className="sm:col-span-2">
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-2">Ciblage par plan (vide = tous)</label>
            <div className="flex gap-2 flex-wrap">
              {PLANS.map(p => (
                <button key={p} onClick={() => togglePlan(p)}
                  className={`px-3 py-1.5 rounded border text-[10px] font-mono transition-all ${
                    form.plans.includes(p) ? 'bg-orange-950/40 border-orange-700/50 text-orange-300' : 'bg-zinc-900 border-zinc-800 text-zinc-600'
                  }`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Annuler</button>
        <button onClick={handleSubmit} disabled={!form.key.trim()}
          className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs disabled:opacity-30">
          Créer
        </button>
      </div>
    </div>
  );
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauRegles() {
  const { rules, summary, loading, error, fetchAll, updateRule, createRule, deleteRule, reloadCache, blockIp, unblockIp } = useRulesData();
  const [activeCategory, setActiveCategory] = useState('all');
  const [search,   setSearch]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const showFb = (type, msg) => { setFeedback({ type, msg }); setTimeout(() => setFeedback(null), 4000); };

  const filtered = useMemo(() => {
    return rules.filter(r => {
      const matchCat = activeCategory === 'all' || r.category === activeCategory;
      const matchQ   = !search || r.key.includes(search.toLowerCase()) || r.label?.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchQ;
    });
  }, [rules, activeCategory, search]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <div className="space-y-5 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">RÈGLES & FEATURE FLAGS</h2>
          {summary && (
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
              {summary.total} règles · {summary.activeFlags} flags actifs · {summary.inactiveFlags} inactifs
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => reloadCache().then(() => showFb('success', 'Cache rechargé'))}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 text-xs">
            <RotateCcw size={12} />
            Reload cache
          </button>
          <button onClick={fetchAll} disabled={loading}
            className="p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-700 hover:border-orange-600/40 rounded text-zinc-300 text-xs">
            <Plus size={12} />
            Nouvelle règle
          </button>
        </div>
      </div>

      {/* KPIs feature flags */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Règles totales', value: summary.total,              color: 'text-zinc-300'    },
            { label: 'Flags actifs',   value: summary.activeFlags,        color: 'text-emerald-400' },
            { label: 'Flags inactifs', value: summary.inactiveFlags,      color: 'text-zinc-500'    },
          ].map(k => (
            <div key={k.label} className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-3 text-center">
              <div className={`text-2xl font-black ${k.color}`}>{k.value}</div>
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="px-4 py-3 bg-red-950/30 border border-red-800/30 rounded-xl text-red-300 text-xs flex gap-2"><AlertTriangle size={14}/>{error}</div>}
      <Feedback {...(feedback||{})} onClear={() => setFeedback(null)} />

      {showForm && (
        <FormNouvelleRegle
          onSubmit={async (data) => { await createRule(data); showFb('success', `Règle "${data.key}" créée`); }}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex bg-zinc-900/50 border border-zinc-800/60 rounded-xl p-1 gap-1 flex-wrap">
          {CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
                activeCategory === cat.id ? 'bg-orange-950/50 border border-orange-700/40 text-orange-300' : 'text-zinc-600 hover:text-zinc-300'
              }`}>
              <cat.icon size={11} />
              <span>{cat.label}</span>
              {summary?.byCategory?.[cat.id] && (
                <span className="text-[9px] bg-zinc-700/60 px-1.5 rounded-full">{summary.byCategory[cat.id]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-36">
          <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filtrer..."
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-200 focus:border-orange-600/50 outline-none" />
        </div>
      </div>

      {/* Contenu */}
      {loading && rules.length === 0 ? (
        <div className="text-center py-10 text-zinc-700 text-sm">Chargement...</div>
      ) : (
        <div className="space-y-4">
          {activeCategory === 'waf' ? (
            <SectionWAF
              rules={filtered}
              onUpdate={updateRule}
              onBlockIp={blockIp}
              onUnblockIp={unblockIp}
            />
          ) : (
            Object.entries(grouped).map(([cat, catRules]) => {
              const catConfig = CATEGORIES.find(c => c.id === cat);
              return (
                <div key={cat} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-zinc-800/40 flex items-center gap-2 bg-black/20">
                    {catConfig && <catConfig.icon size={12} className="text-zinc-600" />}
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{catConfig?.label || cat}</span>
                    <span className="text-[9px] bg-zinc-800 text-zinc-600 px-1.5 rounded-full ml-1">{catRules.length}</span>
                  </div>
                  <div className="divide-y divide-zinc-800/20">
                    {catRules.map(rule => (
                      <RuleRow
                        key={`${rule.category}:${rule.key}`}
                        rule={rule}
                        onUpdate={updateRule}
                        onDelete={async (cat, key) => {
                          await deleteRule(cat, key);
                          showFb('success', `Règle supprimée`);
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
